import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { m as motion } from 'framer-motion';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import { NAV_ITEMS } from './navigation';
import { can } from '@/lib/permissions';
import { useUnreadMessages } from '@/features/messages/useUnread';
import type { Role } from '@/types/domain';
import { cn } from '@/lib/cn';

const GROUPS: { id: 'main' | 'finance' | 'system'; collapsible: boolean }[] = [
  { id: 'main', collapsible: false },
  { id: 'finance', collapsible: true },
  { id: 'system', collapsible: true },
];

// Ley de Hick (auditoría 9-oct): el administrador veía 23 opciones de golpe.
// «Operación» (el trabajo diario) queda siempre a la vista; «Finanzas» y
// «Sistema» se pliegan y solo se abren solos si la página actual vive ahí.
// La elección de cada persona se recuerda en este equipo.
const OPEN_KEY = 'vaultex.navOpen';
const HICK_MAX = 12;
const readOpen = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem(OPEN_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
};

/** Premium glass sidebar with role-aware navigation. */
export function Sidebar({ role }: { role: Role }) {
  const { t } = useTranslation();
  const unread = useUnreadMessages(can(role, 'messages.use'));
  const { pathname } = useLocation();
  const [open, setOpen] = useState<Record<string, boolean>>(readOpen);
  const toggle = (id: string, next: boolean) => {
    const value = { ...open, [id]: next };
    setOpen(value);
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(value));
    } catch {
      // sin storage: vale para esta visita
    }
  };

  return (
    <aside className="hidden w-72 shrink-0 flex-col gap-6 p-4 lg:flex">
      <div className="flex items-center gap-3 px-2 py-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-gradient shadow-glow">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-display text-lg font-bold leading-none text-gradient">VaulTex</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{t('brand.slogan')}</p>
        </div>
      </div>

      <nav className="glass flex-1 space-y-5 overflow-y-auto rounded-2xl p-3">
        {GROUPS.map((group, _i, groups) => {
          const items = NAV_ITEMS.filter(
            (i) => i.group === group.id && (!i.capability || can(role, i.capability)),
          );
          if (items.length === 0) return null;
          const hasActive = items.some((i) => i.to !== '/' && pathname.startsWith(i.to));
          // Solo se pliega cuando hay muchas opciones (el equipo); un cliente
          // con ~10 opciones las ve todas.
          const total = NAV_ITEMS.filter((i) => groups.some((g) => g.id === i.group) && (!i.capability || can(role, i.capability))).length;
          const collapsible = group.collapsible && total > HICK_MAX;
          const expanded = !collapsible || hasActive || Boolean(open[group.id]);
          const label = t(`nav.groups.${group.id}`);
          return (
            <div key={group.id}>
              {collapsible ? (
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={`nav-group-${group.id}`}
                  onClick={() => toggle(group.id, !expanded)}
                  disabled={hasActive}
                  className="focus-ring flex w-full items-center justify-between rounded-lg px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground disabled:cursor-default disabled:hover:text-muted-foreground"
                >
                  <span>{label} <span className="normal-case tracking-normal">· {items.length}</span></span>
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} aria-hidden="true" />
                </button>
              ) : (
                <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
              )}
              {expanded && <ul id={`nav-group-${group.id}`} className="space-y-1">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors',
                          isActive
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            // 2-nov (bundle): sin layoutId, framer-motion carga
                            // solo domAnimation (sin el motor de layout).
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="absolute inset-0 -z-10 rounded-xl bg-brand-gradient/15 ring-1 ring-primary/30"
                            />
                          )}
                          <item.icon className="h-[18px] w-[18px]" />
                          <span>{t(item.labelKey)}</span>
                          {item.to === '/messages' && unread > 0 && (
                            <span
                              className="ml-auto rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-primary"
                              aria-label={t('messages.unreadAria', { count: unread })}
                            >
                              {unread}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
